import type { JSX } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { FeatureFlagClient } from './FeatureFlagClient';
import { INITIALIZATION_TIMEOUT_MS } from './providers/initializationTimeout';
import {
  type FeatureFlagContext,
  type FeatureFlagProviderProps,
  type FlagOverride,
  type FlagValue,
  type HookOptions,
  type OnError,
} from './types';

const EMPTY_DEFAULTS: FeatureFlagProviderProps['defaults'] = {};

// Grace on top of provider init for a late delivery to land. The deadline is
// derived, not a bare 15s: init burns its own timeout first, so a hard-coded
// budget smaller than that would expire on the first commit after the client
// lands and report defaults as final.
const LATE_DELIVERY_GRACE_MS = 5_000;

// Total budget per client, measured from when that client's init starts.
// Deliberately a budget and not a post-init timer: a timer armed after init
// would make the two additive. Past this we treat defaults as the answer
// rather than gate the UI forever on a client that may never connect.
const FINAL_VALUES_DEADLINE_MS =
  INITIALIZATION_TIMEOUT_MS + LATE_DELIVERY_GRACE_MS;

const contextInitialValue: FeatureFlagContext = {
  useIsFeatureActive: (_) => [false, false],
  useFlagValue: <T extends FlagValue = string>(_: string): T => '' as T,
  useAllFlags: () => ({}),
  useAllOverrides: () => [],
  overrides: {},
  setOverride: () => {},
  setOverrides: () => {},
  removeOverride: () => {},
  clearOverrides: () => {},
  hasFinalValues: false,
};

// Deliberately not named `FeatureFlagContext`: that is also the name of the
// value-shape type imported above. Both are legal in the source, where types
// and values are separate namespaces, but the declaration build flattens them
// into one file and renames the collision, which left the exported hook's
// return type pointing at this value instead of the type — silently widening
// it to `any` for every consumer.
const FeatureFlagReactContext = createContext(contextInitialValue);

export const useFeatureFlagsContext = () => useContext(FeatureFlagReactContext);

export const FeatureFlagProvider = ({
  children,
  config,
  onError,
  defaults = EMPTY_DEFAULTS,
}: FeatureFlagProviderProps): JSX.Element => {
  const [hasFinalValues, setHasFinalValues] = useState(false);
  const [client, setClient] = useState<FeatureFlagClient | null>(null);
  const [overridesVersion, setOverridesVersion] = useState(0);

  // Bumped on every delivery so the flag-reading callbacks get a fresh identity
  // and consumers repaint. Readiness alone cannot do this: it latches once, so
  // a delivery landing after the deadline would change no state at all.
  const [flagsVersion, setFlagsVersion] = useState(0);

  // When the current client's budget started. Reset per client, not per mount:
  // apps swap `config` at login (anonymous -> authenticated) without remounting,
  // and a mount-anchored budget would already be spent by then, so the
  // authenticated client — the only one whose values matter — would report its
  // defaults as final immediately.
  const budgetStartedAtRef = useRef(Date.now());

  // `hasFinalValues` means "every provider has given its answer", not "we
  // finished trying" — a timed-out init resolves create() successfully while
  // LaunchDarkly is still connecting, and its values usually land a few seconds
  // later. Consumers gate irreversible work (redirects, query skips) on this, so
  // it must not flip true while the values are still defaults.
  useEffect(() => {
    if (!client) {
      return;
    }

    const unsubscribe = client.onConfigurationChanged(() => {
      // Latch, never un-latch: `hasFinalValues` aggregates across providers, so
      // a second provider still pending would otherwise drag the signal back to
      // false after consumers have already acted on it.
      setHasFinalValues((wasFinal) => wasFinal || client.hasFinalValues);
      setFlagsVersion((version) => version + 1);
    });

    // ConfigurationChanged can land between create() resolving and this effect
    // running; the underlying `ready` fires once and is never replayed, so
    // re-read the settled state rather than wait for an event that is gone.
    if (client.hasFinalValues) {
      setHasFinalValues(true);
    }

    return unsubscribe;
  }, [client]);

  useEffect(() => {
    if (!client) {
      return;
    }

    const remaining =
      FINAL_VALUES_DEADLINE_MS - (Date.now() - budgetStartedAtRef.current);

    if (remaining <= 0) {
      setHasFinalValues(true);
      return;
    }

    const timer = setTimeout(() => setHasFinalValues(true), remaining);
    return () => clearTimeout(timer);
  }, [client]);

  // Stable ref so the effect cleanup can cancel without stale closure issues
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    budgetStartedAtRef.current = Date.now();

    // Drop SDK errors that settle after the provider has been unmounted —
    // LaunchDarkly's `close()` does not abort a pending `waitForInitialization`,
    // so a timeout can still fire against an owner that no longer exists.
    const guardedOnError: OnError = (error) => {
      if (cancelledRef.current) {
        console.warn(
          '[FeatureFlagProvider] Dropping post-unmount SDK error:',
          error.message,
        );
        return;
      }
      onError?.(error);
    };

    const clientPromise = FeatureFlagClient.create(
      config,
      defaults,
      guardedOnError,
    );

    clientPromise.then((newClient) => {
      if (!cancelledRef.current) {
        setClient(newClient);
        setHasFinalValues(newClient.hasFinalValues);
      }
    });

    return () => {
      cancelledRef.current = true;
      setClient(null);
      setHasFinalValues(false);
      // Close the SDK clients so streaming connections, timers, and pending
      // work don't outlive this provider. Runs whether the create() promise
      // has settled or is still pending.
      clientPromise
        .then((oldClient) => oldClient.close())
        .catch(() => {
          // A rejected create() already reported via onError.
        });
    };
    // oxlint-disable-next-line exhaustive-deps
  }, [config]);

  const useIsFeatureActive: FeatureFlagContext['useIsFeatureActive'] =
    useCallback(
      (feature: string, options: HookOptions = {}) => {
        if (!hasFinalValues || !client) {
          const defaultValue = (defaults[feature] as boolean) ?? false;
          return [defaultValue, false] as [boolean, boolean];
        }

        return [client.isFeatureActive(feature, options), true] as [
          boolean,
          boolean,
        ];
      },
      // oxlint-disable-next-line react-hooks/exhaustive-deps
      [hasFinalValues, client, defaults, flagsVersion],
    );

  const useFlagValue: FeatureFlagContext['useFlagValue'] = useCallback(
    <T extends FlagValue = string>(
      feature: string,
      options: HookOptions = {},
    ): T => {
      if (!hasFinalValues || !client) {
        const defaultValue = (defaults[feature] ?? '') as T;
        return defaultValue;
      }

      return client.getFlagValue(feature, options) as T;
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [hasFinalValues, client, defaults, flagsVersion],
  );

  const useAllFlags: FeatureFlagContext['useAllFlags'] = useCallback(
    (options: HookOptions = {}) => {
      if (!hasFinalValues || !client) {
        return defaults;
      }

      return client.getAllFlags(options);
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [hasFinalValues, client, defaults, flagsVersion],
  );

  const useAllOverrides: FeatureFlagContext['useAllOverrides'] =
    useCallback(() => {
      if (!hasFinalValues || !client) {
        return [];
      }
      return client.getAllOverrides();
      // oxlint-disable-next-line react-hooks/exhaustive-deps
    }, [hasFinalValues, client, overridesVersion]);

  const setOverride: FeatureFlagContext['setOverride'] = useCallback(
    (override: FlagOverride) => {
      if (!client) {
        console.warn('[FeatureFlagProvider] Client not available');
        return;
      }
      client.setOverride(override);
      setOverridesVersion((version) => version + 1);
    },
    [client],
  );

  const removeOverride: FeatureFlagContext['removeOverride'] = useCallback(
    (key: string) => {
      if (!client) {
        console.warn('[FeatureFlagProvider] Client not available');
        return;
      }
      client.removeOverride(key);
      setOverridesVersion((version) => version + 1);
    },
    [client],
  );

  const clearOverrides: FeatureFlagContext['clearOverrides'] =
    useCallback(() => {
      if (!client) {
        console.warn('[FeatureFlagProvider] Client not available');
        return;
      }
      client.clearOverrides();
      setOverridesVersion((version) => version + 1);
    }, [client]);

  const overrides: FeatureFlagContext['overrides'] = useMemo(() => {
    if (!client) {
      return {};
    }
    return client.overrides;
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [client, overridesVersion]);

  const setOverrides: FeatureFlagContext['setOverrides'] = useCallback(
    (flagsOrUpdater) => {
      if (!client) {
        console.warn('[FeatureFlagProvider] Client not available');
        return;
      }
      client.setOverrides(flagsOrUpdater);
      setOverridesVersion((version) => version + 1);
    },
    [client],
  );

  const contextValue: FeatureFlagContext = useMemo(
    () => ({
      useIsFeatureActive,
      useFlagValue,
      useAllFlags,
      useAllOverrides,
      overrides,
      setOverride,
      setOverrides,
      removeOverride,
      clearOverrides,
      hasFinalValues,
    }),
    [
      useIsFeatureActive,
      useFlagValue,
      useAllFlags,
      useAllOverrides,
      overrides,
      setOverride,
      setOverrides,
      removeOverride,
      clearOverrides,
      hasFinalValues,
    ],
  );

  return (
    <FeatureFlagReactContext.Provider value={contextValue}>
      {children}
    </FeatureFlagReactContext.Provider>
  );
};
